﻿import { IllustratorArtType } from './enums';
import { NS } from './namespaces';

export type PanelGeneratorVersion = "1" | "2" | "latest";

export const PANEL_DESIGNER_VERSION = 2;
export const DEFAULT_PANEL_GENERATOR_VERSION: PanelGeneratorVersion = "2";

/** ID of the root <metadata> element holding the Panel Designer (classic) spot colour table. */
export const PD_COLOUR_TABLE_ID = "pd-colour-table";

export const FILLABLE_ELEMENTS = [
    "circle",
    "ellipse",
    "path",
    "polygon",
    "polyline",
    "rect",
    "text",
    "textPath",
    "tspan"
];

export const STROKABLE_ELEMENTS = [
    "line",
    ...FILLABLE_ELEMENTS
];

export const RENDERABLE_ELEMENTS = [
    "image",
    "use",
    ...STROKABLE_ELEMENTS,
];

export const VISIBLE_ELEMENTS = [
    "a",
    "foreignObject",
    "g",
    "svg",
    ...RENDERABLE_ELEMENTS
]

export const ELEMENT_TAG_TO_NAME = {
    "g": "Group",
    "use": "External Resource",
    "line": "Line",
    "square": "Square",
    "rect": "Rectangle",
    "circle": "Circle",
    "ellipse": "Ellipse",
    "polygon": "Polygon",
    "polyline": "Polyline",
    "path": "Path",
    "svg": "SVG Document",
    "text": "Text"
};

export function GetElementTypeFriendlyName(element: Element) {
    const tagName = element?.tagName.toLowerCase() ?? "";

    if (tagName === "use") {
        if (element.hasAttribute("zaa:barcode-types")) {
            return "Barcode Placeholder";
        } else if (element.hasAttribute("zaa:component-reference")) {
            return "Component";
        }
    } else if (tagName === "g" && element.hasAttribute("zaa:group-type")) {
        const groupType = element.getAttribute("zaa:group-type")?.trim().toUpperCase() ?? "";
        switch (groupType) {
            case "LAYER":
                return "Layer";
            case "GROUP":
                return "Group";
            case "COMPOUND-PATH":
                return "Compound Path";
        }
    }

    return ELEMENT_TAG_TO_NAME[tagName];
}

export const LEGACY_FILL_ATTRIBUTES = ["pd-fill", "pe-spot-fill", "pe-reg-fill"];
export const LEGACY_STROKE_ATTRIBUTES = ["pd-stroke", "pe-spot-stroke", "pe-reg-stroke"];

export const BLAZOR_ASSEMBLY = "Trident.ZenAutoArt.Designer";

export const VALID_ART_TYPES_FOR_ELEMENTS: { [key: string]: IllustratorArtType[] } = {
    "a": [],
    "circle": [IllustratorArtType.Path, IllustratorArtType.CompoundPath],
    "ellipse": [IllustratorArtType.Path, IllustratorArtType.CompoundPath],
    "foreignObject": [IllustratorArtType.Component],
    "image": [IllustratorArtType.Image],
    "line": [IllustratorArtType.Path, IllustratorArtType.CompoundPath],
    "path": [IllustratorArtType.Path, IllustratorArtType.CompoundPath],
    "polygon": [IllustratorArtType.Path, IllustratorArtType.CompoundPath],
    "polyline": [IllustratorArtType.Path, IllustratorArtType.CompoundPath],
    "rect": [IllustratorArtType.Path, IllustratorArtType.CompoundPath],
    "svg": [],
    "text": [IllustratorArtType.TextArtUnsupported, IllustratorArtType.TextPathArtUnsupported, IllustratorArtType.TextRunArtUnsupported, IllustratorArtType.TextFrameArt, IllustratorArtType.LegacyTextArt],
    "textPath": [IllustratorArtType.TextArtUnsupported, IllustratorArtType.TextPathArtUnsupported, IllustratorArtType.TextRunArtUnsupported, IllustratorArtType.TextFrameArt, IllustratorArtType.LegacyTextArt],
    "tspan": [IllustratorArtType.TextArtUnsupported, IllustratorArtType.TextPathArtUnsupported, IllustratorArtType.TextRunArtUnsupported, IllustratorArtType.TextFrameArt, IllustratorArtType.LegacyTextArt],
    "use": []
};
